<template>
    <popup
      ref="popup"
      :button-style="duplicate ? 'info' : buttonStyle"
      :heading="drawerHeading"
      :button-text="label || (duplicate ? duplicateLabel : (rowData ? '' : buttonText))"
      :plain="duplicate && !!duplicateLabel"
      center-button
      :disable-button="disableButton || !isManager('finance|management|admin')"
      :button-icon="duplicate ? 'fa fa-clone' : (rowData ? 'fa fa-edit' : buttonIcon)"
      :button-aria-label="duplicate ? 'Duplicate rate' : (rowData ? 'Edit rate' : null)"
      :button-title="duplicate ? 'Duplicate rate' : (rowData ? 'Edit rate' : null)"
      :show-save="!publishOnly"
      :show-save-publish="(allowPublish || publishOnly) && view === 'sharer'"
      :before-save="prepareRow"
      :keep-open-until-saved="true"
      @save="onSave"
      @save-publish="onSavePublish"
      @open="onOpen"
      @close="onClose">

      <el-form v-if="showRange && view !== 'sharer'" label-position="top">

          <el-row :gutter="12">

              <el-col :sm="24" :md="24":lg="18" align="center">
                <el-form-item label="Tenor Range (months)">
                  <el-slider
                     v-model="months"
                     range
                     show-stops
                     :step="6"
                     :min="0"
                     :max="maxMonths"
                     :marks="marks"
                      @change="updateMonths"
                      :disabled="disabled">
                   </el-slider>
                 </el-form-item>
              </el-col>

              <el-col :sm="24" :md="24" :lg="6" align="center">
                <el-form-item :label="plural('Year', years)">
                  <timeline :months="months" :max-months="maxMonths" :periods="periods" />
                </el-form-item>
              </el-col>

          </el-row>

      </el-form>

      <component :is="comp" :key="rowData ? rowData.id : 'new'" ref="rateDetails" :row-data="row" />

    </popup>

</template>



<script>

import axios from '@/lib/axiosClient'
import SiteMixins from '@/mixins/SiteMixins'

import SharerRateDetails from './SharerRateDetails'
import TenancyRateDetails from './TenancyRateDetails'
import UtilityRateDetails from './UtilityRateDetails'

import Timeline from './Timeline'

export default {

  mixins: [SiteMixins],

  components: {
    Timeline
  },

  props: {

    view: {
      type: String,
      default: 'sharer'
    },

    rowData: {
      type: Object,
      default: null
    },

    showRange: {
      type: Boolean,
      default: true
    },

    tableRef: {
      type: String,
      default () { return this.view + 'ratesttable' }
    },

    duplicate: {
      type: Boolean,
      default: false
    },

    duplicateLabel: {
      type: String,
      default: ''
    },

    disableButton: {
      type: Boolean,
      default: false
    },

    allowPublish: {
      type: Boolean,
      default: false
    },

    publishOnly: {
      type: Boolean,
      default: false
    },

    label: {
      type: String,
      default: ''
    }

  },

  methods: {

    init () {

      switch (this.view) {
        case 'sharer':
              this.buttonText = 'Sharer'
              this.buttonIcon = 'fa fa-users'
              this.buttonStyle = 'warning'
              this.rateGroupPrefix = 80000
              this.comp = SharerRateDetails
              break

        case 'tenancy':
              this.buttonText = 'Tenancy'
              this.buttonIcon = 'fa fa-home'
              this.buttonStyle = 'success'
              this.rateGroupPrefix = 30000
              this.comp = TenancyRateDetails
              break

        case 'utility':
              this.buttonText = 'Utility'
              this.buttonIcon = 'fa fa-plug'
              this.buttonStyle = 'primary'
              this.rateGroupPrefix = 50000
              this.comp = UtilityRateDetails
              break

      }

      if (this.duplicate && this.rowData) {
        // Duplicate mode: deep-clone the source row, strip id + tier ids,
        // keep seqNo, append " (copy)" to name. row.id === null → onSave POSTs.
        var cloned = this.clone(this.rowData)
        delete cloned.id
        cloned.name = (cloned.name || '') + ' (copy)'
        if (cloned.tiers && Array.isArray(cloned.tiers)) {
          cloned.tiers = cloned.tiers.map(function (t) {
            var tier = Object.assign({}, t)
            delete tier.id
            return tier
          })
        }
        this.row = cloned
      } else if (!this.row) {
        this.row = this.getTableStruct('rates')
        this.row.status = 'Active'
        this.row.groupId = this.rateGroupPrefix
        this.row.type = this.buttonText
      }

      if (this.row && (this.row.fromMonth || this.row.toMonth)) {
        this.months = []
        this.months.push(this.row.fromMonth)
        this.months.push(this.row.toMonth)
      }

    },

    updateMonths(months) {

      if (this.isArray(months)) {

          this.months = months
          this.row.fromMonth = months[0]
          this.row.toMonth = months[1]
        }

    },

    prepareRow () {
      // For sharer view: validate tiers before saving.
      // Returns true if ready to save, false if validation failed (message already shown).
      if (this.view === 'sharer' && this.$refs.rateDetails) {
        var result = this.$refs.rateDetails.validateTiers()
        if (!result.valid) {
          // Propagate per-tier errors into the child for display
          this.$refs.rateDetails.setTierErrors(result.errors)
          this.$message.error('Fix the highlighted errors before saving.')
          return false
        }
        // Clear errors on valid
        this.$refs.rateDetails.setTierErrors([])

        // XOL-462 Step 7 / XOL-486: normalise the header fields the sharer-rate
        // form owns. number-input can emit strings, and the API's
        // effectiveToSharer does a strict (int) $raw != $raw check. Runs on the
        // Save path AND the Save+Publish path — onSavePublish already calls
        // prepareRow() first.
        this.row.strictPosition = !!this.row.strictPosition
        this.row.fromSharer = Number(this.row.fromSharer) || 1
        this.row.toSharer = Number(this.row.toSharer) || 10
        this.row.strictTerm = !!this.row.strictTerm

        // Serialize tiers: coerce numbers.
        //
        // XOL-464 hide-but-store — DELIBERATELY UNCHANGED: this map reads every
        // telco1..telco10 off row.tiers regardless of the header's effective Min,
        // because row.tiers holds every position whether the tier editor rendered
        // it or not. Do NOT gate any of these ten lines on fromSharer/toSharer —
        // writing 0 into a sub-Min position here would silently destroy a real,
        // previously-billing rate on the very next save of an EXISTING rate,
        // triggered by a save the user made for an unrelated reason (e.g. editing
        // Height). A new rate and an edited rate both already land at 0.0000 (the
        // column default) for a never-filled sub-Min position, so hide-but-store
        // and "write 0" are indistinguishable for a new rate — they diverge only
        // here, on an edit, which is exactly why this is the single most likely
        // place a later "cleanup" reintroduces the bug. See
        // SharerRateDetails.vue's effectiveFromSharer / the tier-completeness
        // loop for where the hidden-but-required distinction actually lives.
        var tiers = this.row.tiers
        if (tiers && tiers.length > 0) {
          this.row.tiers = tiers.map(function (t) {
            var out = {
              seqNo: Number(t.seqNo),
              fromMonth: Number(t.fromMonth),
              toMonth: Number(t.toMonth),
              telco1: Number(t.telco1) || 0,
              telco2: Number(t.telco2) || 0,
              telco3: Number(t.telco3) || 0,
              telco4: Number(t.telco4) || 0,
              telco5: Number(t.telco5) || 0,
              telco6: Number(t.telco6) || 0,
              telco7: Number(t.telco7) || 0,
              telco8: Number(t.telco8) || 0,
              telco9: Number(t.telco9) || 0,
              telco10: Number(t.telco10) || 0,
              mwRate: Number(t.mwRate) || 0,
              rruRate: Number(t.rruRate) || 0,
              rruSet: Number(t.rruSet) || 0
            }
            return out
          })

          // Mirror tier-1 onto flat columns for backend transition read-compat
          var tier1 = this.row.tiers[0]
          this.row.fromMonth = tier1.fromMonth
          this.row.toMonth = tier1.toMonth
          this.row.telco1 = tier1.telco1
          this.row.telco2 = tier1.telco2
          this.row.telco3 = tier1.telco3
          this.row.telco4 = tier1.telco4
          this.row.telco5 = tier1.telco5
          this.row.telco6 = tier1.telco6
          this.row.telco7 = tier1.telco7
          this.row.telco8 = tier1.telco8
          this.row.telco9 = tier1.telco9
          this.row.telco10 = tier1.telco10
          this.row.mwRate = tier1.mwRate
          this.row.rruRate = tier1.rruRate
          this.row.rruSet = tier1.rruSet
        }
      }
      return true
    },

    // XOL-487: reads this.$refs.popup and no-ops when it is absent, or when
    // the popup stub doesn't implement the new handshake (existing specs
    // provide vm.$refs = {} or a popup stub with only closeModal — both
    // must keep passing unmodified).
    finishSave (ok) {
      var p = this.$refs.popup
      if (!p || typeof p.saveFailed !== 'function') return

      if (ok) {
        p.saveSucceeded()
      } else {
        p.saveFailed()
      }
    },

    // XOL-487: every field prepareRow() can fail on sits above the fold — a
    // user scrolled down to the tier cards otherwise gets "fix the
    // highlighted errors" pointing off-screen. No-op when the popup ref is
    // absent or doesn't implement scrollToTop.
    scrollPopupTop () {
      var p = this.$refs.popup
      if (p && typeof p.scrollToTop === 'function') p.scrollToTop()
    },

    onSave () {
      // XOL-487: this branch is unreachable while :before-save="prepareRow" is
      // wired — Popup.saveModal()'s beforeSave gate returns before @save is
      // emitted, so onSave() never runs with a failing prepareRow() in the
      // first place (the pre-existing `if (!this.prepareRow()) return` here
      // was dead for the identical reason). It stays, and stays paired with
      // finishSave(false), because it is what makes the dead branch SAFE
      // rather than merely dead: were it ever reached under
      // keepOpenUntilSaved with no finishSave(false) call, the drawer would
      // hang in "Saving…" forever with no server round trip to clear it. The
      // scroll-to-top-on-blocked-save behaviour actually shipped lives in
      // Popup.saveModal() (see the beforeSave gate there) — a future cleanup
      // pass is at least as likely to delete the live copy there as this
      // dead-but-safe one here.
      if (!this.prepareRow()) {
        this.scrollPopupTop()
        return this.finishSave(false)
      }

      var self = this
      this.saveForm(this, '/rates', this.row.id, this.row, this.buttonText, function (response) {
        self.finishSave(true)
        self.notifyPublishAdvice()
        self.$emit('saved', response)
      }, function () {
        self.finishSave(false)
      }, true, this.tableRef, true)
    },

    // XOL-497 Step 10 — the post-save nudge.
    //
    // Deliberately NOT called from onSavePublish(): that path just published,
    // so telling the user to publish would be nonsense.
    //
    // It is a notification rather than a save-time $confirm because
    // Popup.saveModal() gates on beforeSave() SYNCHRONOUSLY; making it async
    // would change a shared atom's save contract for ~40 consumers. The
    // inline alert covers "before you commit"; this covers after.
    //
    // At most ONE toast ever fires. Two stacked warnings on a save that
    // succeeded is a punishment, not feedback — and the two conditions are
    // mutually exclusive anyway (one needs no published version, the other
    // needs one).
    notifyPublishAdvice () {
      if (this.view !== 'sharer') return

      var child = this.$refs.rateDetails
      if (!child || typeof child.publishAdvice !== 'function') return

      // publishAdvice() already returns both flags false when the publish
      // lookup failed or is still in flight, so an unknown state stays
      // silent here rather than guessing. Nothing extra to check.
      var advice = child.publishAdvice()

      if (advice.needsPublish) {
        this.$notify({
          title: 'Saved as a draft',
          message: 'This rate has never been published, so it won\'t appear in the licensee rate picker and can\'t be attached yet. Publish it from Manage versions on the rate\'s row.',
          type: 'warning',
          duration: 7000
        })
        return
      }

      if (advice.minChanged) {
        // XOL-497 v2. The v1 message ended "Publish from Manage versions on the
        // rate's row" — instructing an action that does NOT achieve the stated
        // outcome, because publishing never re-pins a licensee. A toast that
        // sends someone to do the wrong thing is worse than no toast, since
        // they will believe the problem is handled.
        //
        // NOTE: publishAdvice() does NOT check whether anyone is pinned — it
        // returns `known && hasPublishedVersion && minChanged`. An earlier
        // revision of this comment claimed it did, a leftover from v2 when a
        // pinnedCount gate existed; v3 deleted that count deliberately. The
        // toast is safe without it because it states a rule rather than
        // asserting anyone is affected.
        this.$notify({
          title: 'Existing licensees keep their current Min',
          // XOL-497, widened 2026-09-14: the re-pin step used to name only the
          // per-licensee Finance tab, because the bulk tool could not re-pin a
          // licensee already on the rate. API XOL-510 + XOL-511 (merged
          // a6f7780) fixed both its paths, so routing users one-at-a-time is
          // now needlessly narrow. Kept in step with the drawer alert in
          // SharerRateDetails, which carries the evidence for the change.
          // Copy and its access gating live in minNudgeMessage() above — one
          // account of it, not two. (A v4 comment here described the split as
          // being only about the bulk clause; round five disproved that.)
          message: this.minNudgeMessage(),
          type: 'warning',
          duration: 7000
        })
      }
    },

    onSavePublish () {
      // XOL-376: validate BEFORE the publish confirmation. Showing the warning
      // first and a validation toast after it read backwards — an incomplete
      // form now blocks (with the inline errors highlighted) without ever
      // reaching the confirm. The el-message-box is modal, so the form cannot
      // change between this check and the confirm click — no re-validation
      // needed inside .then.
      if (!this.prepareRow()) {
        this.scrollPopupTop()
        return this.finishSave(false)
      }

      var self = this
      this.$confirm(
        // XOL-497: folded in. This predates the ticket and was wrong for the
        // same reason v1/v2 of the Min copy were — it promised a recompute that
        // publish() does not dispatch, for a "following the current rate"
        // population the product does not allow. It sits on the same drawer as
        // the Min alert, so leaving it would have the drawer contradict itself.
        'Publish creates a new immutable version of this rate and makes it the current version. New attachments and re-pins will get it. Licensees keep the version they\'re pinned to — publishing does not move them. Continue?',
        'Save + Publish',
        {
          confirmButtonText: 'Save + Publish',
          cancelButtonText: 'Cancel',
          type: 'warning',
          center: true
        }
      ).then(function () {
        self.saveForm(self, '/rates', self.row.id, self.row, self.buttonText, function (rate) {
          var id = rate && (rate.id || (rate.data && rate.data.id))
          self.send(
            self,
            self.getEndpoint('/rates/' + id + '/publish'),
            {},
            'rate version',
            function (resp) {
              // XOL-497: re-arm the child's Min baseline. This drawer's
              // instance survives a close/reopen (Popup has no
              // destroy-on-close) and at SharerRateOverride's "Modify" mount
              // the rate id does not change either, so created() never runs
              // again. Without this the just-published Min still counts as
              // "changed" and the drawer keeps warning about it — and the next
              // plain Save fires a toast telling the user to publish something
              // they have already published.
              //
              // Only on the publish path: after a plain Save the draft
              // legitimately still differs from the published version.
              var child = self.$refs.rateDetails
              if (self.view === 'sharer' && child && typeof child.captureMinBaseline === 'function') {
                child.captureMinBaseline()
              }
              self.$emit('saved', rate)
              self.$emit('published', resp)
              // Deliberately closeModal(), not finishSave(true)/saveSucceeded():
              // this popup has no table-ref (see the null tableRef note below),
              // so saveSucceeded()'s tableRef-refresh emit would be a no-op
              // today — but wiring one up later would risk a double refresh,
              // since closeModal() already gets us to "drawer closed" via its
              // own 'close' emit. closeModal() clears savingAction AND emits
              // 'close' (saveSucceeded() does neither of the emit) — two
              // mechanisms for one "we're done, close the drawer" contract,
              // kept deliberately separate rather than reconciled here.
              if (self.$refs.popup) self.$refs.popup.closeModal()
            },
            function () {
              self.finishSave(false)
            },
            axios.post,
            true,
            'Publish',
            self.tableRef,
            true
          )
        // null tableRef on the intermediate save — the publish send below fires the
        // single table refresh, so the resolved row drops once (not twice).
        }, function () {
          self.finishSave(false)
        }, true, null, true)
      }).catch(function () {
        // User cancelled the Save + Publish confirm. finishSave(false) routes
        // to Popup.saveFailed(), which now reads savingAction (still
        // 'save-publish' here — finishSave/saveFailed haven't cleared it yet)
        // to refocus the Save + Publish button rather than Save, since that
        // is the control the user was interacting with.
        self.finishSave(false)
      })
    },

    // Kept in step with SharerRateDetails.minAlertBody, which carries the gate
    // table. Only the bulk clause branches (isSuper); the Finance-tab clause is
    // unconditional, because XOL-516 made that tab's gate a superset of this
    // nudge's own audience. A third branch for readers who could reach neither
    // route existed between those two changes and is gone.
    minNudgeMessage () {
      var fact = 'Saving doesn\'t move licensees off the version they\'re pinned to.'
      var step = ' To give them this Min, publish a new version from Manage versions, then re-pin'

      if (this.isSuper()) {
        return fact + step + ': one licensee on its sharer\'s Finance tab, ' +
          'or several at once with Bulk licensee rate change.'
      }

      // Unconditional since XOL-516 widened the Finance tab's gate to a
      // superset of this nudge's audience. See SharerRateDetails.minAlertBody
      // for the full reasoning and for the third branch that used to be here.
      return fact + step + ' each licensee on its sharer\'s Finance tab.'
    },

    // XOL-497 Step 9 — refresh publish state each time the drawer opens.
    //
    // Popup does NOT use destroy-on-close, so SharerRateDetails survives a
    // close/reopen and its created() runs only once. Without this relay the
    // badge would go stale in a real sequence: open Edit, close, publish from
    // the Manage-versions drawer, reopen Edit — and still read "never
    // published".
    //
    // On the FIRST open the $refs are not populated yet and created() does the
    // fetch, so the typeof guard makes this a no-op rather than a duplicate
    // request. The view check keeps Tenancy and Utility rates untouched: they
    // share this component but render no badge and have no versions endpoint.
    onOpen () {
      var child = this.$refs.rateDetails

      // XOL-498: cleared ABOVE the view gate. The original comment here
      // justified that by claiming the band fields render on every view using
      // this form. **That was false** — review checked it: Min/Max exist only
      // in SharerRateDetails, and the other two views mount
      // TenancyRateDetails / UtilityRateDetails, neither of which references
      // fromSharer, toSharer or this method. On those views the call is a
      // strict no-op caught by the typeof guard, so there is no cross-rate
      // leak here to prevent.
      //
      // The placement is kept anyway, as an ordering pin rather than a fix:
      // clearing stale child state should not sit behind a gate that exists
      // for an unrelated reason (the versions endpoint 404s off-sharer). If
      // SharerRateDetails is ever mounted for another view, or another view
      // grows the same cue, the clear is already in the right place. Moving it
      // below the gate reddens a test, which is what holds that in place.
      if (child && typeof child.clearBandFollowNote === 'function') {
        child.clearBandFollowNote()
      }

      if (this.view !== 'sharer') return
      if (child && typeof child.loadPublishState === 'function') {
        child.loadPublishState()
      }
    },

    onClose () {
//      this.$message('Closing...')
    }

  },

  computed: {

    years: function () {
      return this.formatNumber((this.months[1] - this.months[0]) / 12, 1)
    },

    drawerHeading: function () {
      if (this.duplicate) {
        return 'Duplicate ' + this.titleCase(this.view) + ' Rate'
      }
      return this.buttonText
    }

  },

  watch: {

    // vuetable recycles this component instance across pagination/sort, updating
    // the rowData prop in place on the SAME instance. Without re-syncing, the
    // data().row snapshot stays the first rate ever rendered — so after paging,
    // clicking Edit on a row opened the WRONG rate. Re-sync the working copy
    // here; the :key on the detail <component> re-mounts the form so it
    // re-normalizes its tiers from the new row.
    // Duplicate mode: re-run init() so the cloned seed is rebuilt from the new rowData.
    rowData (val) {
      if (this.duplicate) {
        this.init()
      } else {
        this.row = val || this.getTableStruct('rates')
        if (this.row && (this.row.fromMonth || this.row.toMonth)) {
          this.months = [this.row.fromMonth, this.row.toMonth]
        }
      }
    }

  },



  data () {
    return {
      row: this.duplicate ? null : this.rowData,
      disabled: (this.rowData && !this.duplicate) ? true : false,
      data: [],

      comp: null,
      buttonText: null,
      buttonIcon: null,
      buttonStyle: null,
      heading: null,
      rateGroupPrefix: null,

      months: [0, 36],
      maxMonths: 300,
      periods: 3,

      marks: {
        24: '2 yrs',
        72: '6 yrs',
        120: '10 yrs',
        168: '14 yrs',
        216: '18 yrs',
        264: '22 yrs'
      }

    }
  },

  created () {
    this.init()
  }

}
</script>
